import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  createDrugEnhanced,
  updateDrugEnhanced,
} from "../../../../redux/Thunk/Product/ProductThunks";
import ProductTagsSelector from "../ProductTagsSelector";
import Button from "../../../common/Button";

const DrugModal = ({ isOpen, onClose, drug = null, onSuccess }) => {
  const dispatch = useDispatch();
  const [drugName, setDrugName] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isEdit = Boolean(drug && drug.id);

  useEffect(() => {
    if (drug) {
      setDrugName(drug.drugName || "");
      setSelectedTags(drug.tags || []);
    } else {
      setDrugName("");
      setSelectedTags([]);
    }
    setError("");
  }, [drug, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = drugName.trim();
    if (!trimmed) {
      setError("Please enter a valid drug name.");
      return;
    }

    setLoading(true);
    setError("");

    const tagIds = selectedTags
      .map((t) => (typeof t === "object" ? t.id : Number(t)))
      .filter((id) => !isNaN(id));

    try {
      if (isEdit) {
        await dispatch(
          updateDrugEnhanced({
            id: drug.id,
            drugName: trimmed,
            tagIds,
          })
        ).unwrap();
      } else {
        await dispatch(
          createDrugEnhanced({
            drugName: trimmed,
            tagIds,
          })
        ).unwrap();
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(
        err?.error ||
          err?.message ||
          "Failed to save drug. Please verify the name is unique."
      );
    } finally {
      setLoading(false);
    }
  };

  const assignedCount = Number(drug?.assignedProductCount || 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 transition-opacity">
      <div
        className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-lg flex flex-col max-h-[85vh] my-auto overflow-hidden transform transition-all animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50/80 to-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary-50 border border-primary-100/60 flex items-center justify-center text-primary-600 shadow-2xs">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 leading-tight">
                {isEdit ? "Edit Master Drug" : "Add New Master Drug"}
              </h3>
              <p className="text-xs text-gray-500 font-medium">
                {isEdit
                  ? "Update pharmacological formula & regulatory checkpoints"
                  : "Register active ingredient molecule to inventory master catalog"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Scrollable Form Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5 min-h-0">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs font-semibold text-red-700 flex items-center gap-2">
                <svg
                  className="w-4 h-4 shrink-0 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {isEdit && assignedCount > 0 && (
              <div className="p-3.5 bg-amber-50/80 border border-amber-200/80 rounded-2xl flex items-start gap-3">
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="text-xs text-amber-900 leading-relaxed">
                  <span className="font-bold">
                    Assigned to {assignedCount} product{assignedCount > 1 ? "s" : ""}.
                  </span>{" "}
                  Renaming this drug will automatically synchronize across all{" "}
                  <span className="font-semibold">{assignedCount}</span> linked products in the catalog without breaking composition links.
                </div>
              </div>
            )}

            {/* Drug Name Input */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Drug / Molecule Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                autoFocus
                value={drugName}
                onChange={(e) => setDrugName(e.target.value)}
                placeholder="e.g. Paracetamol 650mg, Amoxicillin 500mg, Metformin SR"
                className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:outline-hidden focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10 transition-all"
              />
              <span className="text-[11px] text-gray-400 mt-1 block">
                Enter generic pharmacological salt, molecule name, or strength formulation.
              </span>
            </div>

            {/* Tags Selector with in-flow expansion */}
            <div>
              <ProductTagsSelector
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                label="Clinical & Regulatory Tags"
                placeholder="Select or add regulatory tags (e.g. Schedule H, Antibiotic, Cold Chain, LASA)..."
                inlineDropdown={true}
              />
            </div>
          </div>

          {/* Modal Footer (Pinned at bottom) */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2.5 bg-gray-50/80 shrink-0">
            <button
              type="button"
              disabled={loading}
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-xs hover:shadow-md transition-all cursor-pointer flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : isEdit ? (
                "Update Drug"
              ) : (
                "+ Create Drug"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DrugModal;
